import { useCallback } from "react";

import type { FormValidationErrors } from "~/shared/hooks/use-form-validation.ts";
import { useFormValidation } from "~/shared/hooks/use-form-validation.ts";
import type { QuoteStateType } from "~/shared/types/quote-details-types.ts";
import { isPastShipDate } from "~/shared/utils/date-helpers.ts";
import { validateHandlingUnitItem } from "~/shared/utils/validate-handling-unit-item.ts";

import type { SelectedAddressBookItems } from "../context/quote-address-context.tsx";

// A stop has a usable location when its address carries a postal code. This is
// intentionally NOT keyed on addressBookItemId: editing an applied saved address
// detaches it (id -> 0), but the address (and its postal code) is still present
// and valid, so the stop must keep passing validation.
function addressItemHasLocation(
	addressBookItem: SelectedAddressBookItems[keyof SelectedAddressBookItems],
): boolean {
	return !!addressBookItem && !!addressBookItem.postalCode?.trim();
}

function validateQuoteState(
	quoteState: QuoteStateType,
	isInternal: boolean,
	selectedAddressBookItems: SelectedAddressBookItems,
): FormValidationErrors {
	const fields = new Set<string>();
	const items: Record<number, Set<string>> = {};

	// Customer – only required for internal users
	if (
		isInternal &&
		(!quoteState.customer?.id || quoteState.customer.id === 0)
	) {
		fields.add("customer");
	}

	// External users may not quote in the past
	if (isPastShipDate(quoteState.shipmentDate, isInternal)) {
		fields.add("shipmentDate");
	}

	// Pickup – valid if a postal code is entered OR the stop's address has one
	if (
		!quoteState.pickupPostalCode?.postalCode &&
		!addressItemHasLocation(selectedAddressBookItems.pickup)
	) {
		fields.add("pickup");
	}

	// Delivery – valid if a postal code is entered OR the stop's address has one
	if (
		!quoteState.deliveryPostalCode?.postalCode &&
		!addressItemHasLocation(selectedAddressBookItems.delivery)
	) {
		fields.add("delivery");
	}

	// Handling unit items
	const isSortAndSegregate = quoteState.deliveryServices.some(
		(service) => service.name === "Sort and Segregate",
	);

	for (const item of quoteState.items) {
		const invalidFields = validateHandlingUnitItem(item, isSortAndSegregate);
		if (invalidFields.size > 0) {
			items[item.id] = invalidFields;
		}
	}

	return { fields, items };
}

export const useQuoteValidation = () => {
	const validationFn = useCallback(
		(
			quoteState: QuoteStateType,
			isInternal: boolean,
			selectedAddressBookItems: SelectedAddressBookItems,
		) => validateQuoteState(quoteState, isInternal, selectedAddressBookItems),
		[],
	);

	return useFormValidation(validationFn);
};
